import type { VuePlugin, VuePlugins } from '../types'

/**
 * Создает карту индексов для элементов массива порядка
 * @param order - Массив строк, определяющий порядок сортировки
 * @returns Объект, где ключи - элементы массива order, а значения - их индексы
 * @example
 * ```typescript
 * const orderMap = createOrderIndexMap(['high', 'medium', 'low']);
 * // Результат: { 'high': 0, 'medium': 1, 'low': 2 }
 * ```
 */
function createOrderIndexMap(order: string[]): Record<string, number> {
  const orderIndexMap: Record<string, number> = {}
  order.forEach((key, index) => {
    orderIndexMap[key] = index
  })
  return orderIndexMap
}

/**
 * Сортирует массив строк в соответствии с заданным порядком
 * @param values - Массив строк для сортировки
 * @param order - Эталонный массив, определяющий желаемый порядок сортировки
 * @returns Новый отсортированный массив строк
 * @example
 * ```typescript
 * const priorities = ['low', 'high', 'medium'];
 * const desiredOrder = ['high', 'medium', 'low'];
 * const sorted = sortArrayByReferenceArray(priorities, desiredOrder);
 * // Результат: ['high', 'medium', 'low']
 * ```
 */
export function sortArrayByReferenceArray(values: string[], order: string[]): string[] {
  const FALLBACK_INDEX = order.length
  const orderIndexMap = createOrderIndexMap(order)

  /**
   * Возвращает индекс сортировки для данного значения
   * @param val - Значение для получения индекса сортировки
   * @returns Индекс в эталонном массиве или fallback индекс для неизвестных значений
   */
  function sortKey(val: string): number {
    return orderIndexMap[val] ?? FALLBACK_INDEX
  }

  return [...values].sort((a, b) => {
    return sortKey(a) - sortKey(b)
  })
}

/**
 * Извлекает имена плагинов из их путей и создает нормализованную карту плагинов
 *
 * Функция принимает объект плагинов, где ключи являются путями к файлам плагинов,
 * и возвращает новый объект с нормализованными именами плагинов в качестве ключей.
 *
 * Процесс нормализации:
 * - Удаляет префикс "./" из начала пути
 * - Удаляет суффикс "/index.ts" из конца пути
 *
 * @param plugins - Объект плагинов Vue, где ключи - пути к файлам плагинов, значения - модули плагинов
 * @param path - Путь до директории с плагинами
 * @returns Новый объект с нормализованными именами плагинов в качестве ключей
 *
 * @example
 * ```typescript
 * const plugins = {'./auth/index.ts': auth, './router/index.ts': router, './store/index.ts': store}
 *
 * const normalized = extractPluginNameFromPath(plugins)
 * // Результат: {'auth': auth,'router': router,'store': store}
 * ```
 */
export function extractPluginNameFromPath(plugins: VuePlugins, path: string = '/src/app/plugins/') {
  const result: VuePlugins = {}

  Object.entries(plugins).forEach(([pluginPath, module]) => {
    const pluginName = pluginPath.replace(path, '').replace('/index.ts', '')
    result[pluginName] = module
  })

  return result
}

export function defineVuePlugin(pluginFn: VuePlugin) {
  return pluginFn
}
